import { appData, state, tempConfigData } from "../../app/state/state.js";
import {
    renderCategoryEditor,
    renderOptionEditor,
    renderSimpleListEditor,
    bindStatusPrioritySettingsControls,
    handleStatusPrioritySettingsButtonClick,
} from "./statusPrioritySettings.js";
import {
    bindTaskSettingsControls,
    renderTaskSettings,
} from "./taskSettings.js";
import {
    renderTaskLinkingTool,
    resetTaskRelinkSettingsState,
} from "./taskRelinkSettings.js";
import {
    bindTypeSettingsControls,
    closeTypeSettingsDropdownsOnOutsideClick,
    handleTypeSettingsButtonClick,
    handleTypeSettingsChange,
    renderTypeEditor,
} from "./typeSettings.js";
import {
    getContrastColor,
    normalizeHexColor,
} from "../../shared/ui/brandingTokens.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createUserOptions } from "../../shared/ui/controls/controlOptions.js";

const STREAM_SCOPE_VALUES_BY_KEY = Object.freeze({
    labStatus: ["internal", "external"],
    labTypes: ["internal", "external"],
    maintenanceStatus: ["service", "issue"],
    maintenanceTypes: ["service", "issue"],
});

function getStreamScopeValues(key) {
    return STREAM_SCOPE_VALUES_BY_KEY[key] || [];
}

function updateStreamScope(item, key, value, checked) {
    const allStreams = getStreamScopeValues(key);
    if (allStreams.length === 0 || !allStreams.includes(value)) {
        return false;
    }

    const selectedStreams = new Set(
        Array.isArray(item.streams) && item.streams.length > 0
            ? item.streams
            : allStreams,
    );

    if (checked) {
        selectedStreams.add(value);
    } else {
        selectedStreams.delete(value);
    }

    if (selectedStreams.size === 0) {
        return false;
    }

    if (selectedStreams.size === allStreams.length) {
        item.streams = [];
    } else {
        item.streams = allStreams.filter((stream) =>
            selectedStreams.has(stream));
    }
    return true;
}

function syncLinkedColourFields(target, value) {
    const linkId = target.closest("[data-link-id]")?.dataset.linkId;
    if (!linkId) {return;}

    const linkedContainer = document.querySelector(`[data-link-id="${linkId}"]`);
    const pickerInput = linkedContainer?.querySelector("[data-role='picker']");
    const textInput = linkedContainer?.querySelector("[data-role='text']");
    const row = target.closest(".settings-config-row");
    const nameInput = row?.querySelector(".settings-config-name--colored");
    const safeColor = normalizeHexColor(value, "#cccccc");

    if (pickerInput && pickerInput !== target) {pickerInput.value = safeColor;}
    if (textInput && textInput !== target) {textInput.value = safeColor;}
    if (nameInput) {
        nameInput.style.backgroundColor = safeColor;
        nameInput.style.color = getContrastColor(safeColor);
    }
}

function bindPatientCaseManagerControl(container) {
    const wrapper = container.querySelector("[data-patient-case-manager-control]");
    if (!wrapper) {return;}
    const hiddenInput = container.querySelector(
        "input[data-config-key='defaultPatientCaseManager']",
    );
    const selectedValue = hiddenInput?.value || "";
    const patientManagers = (appData.users || [])
        .filter((user) =>
            user.id === selectedValue ||
            user.isActive !== false)
        .filter((user) =>
            user.id === selectedValue ||
            user.role === "admin" ||
            (
                Array.isArray(user.permissions) &&
                user.permissions.includes("patients")
            ))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const options = createUserOptions(patientManagers, {
        selectedValues: selectedValue ? [selectedValue] : [],
    });

    createSelectControl({
        root: wrapper,
        accessibleName: "Default case manager",
        value: selectedValue,
        options,
        clearable: true,
        searchable: options.length >= 7,
        searchThreshold: 7,
        portalMenu: true,
        placeholder: "Current user fallback",
        mirror: false,
        onChange: (value) => {
            if (!hiddenInput) {return;}
            hiddenInput.value = value || "";
            hiddenInput.dispatchEvent(new Event("change", {bubbles: true}));
        },
    });
}

/**
 * Resets transient category-settings state such as task-link tool selections.
 *
 * @returns {void}
 */
export function resetCategorySettingsState() {
    resetTaskRelinkSettingsState();
}

/**
 * Renders a category-driven settings tab for an entity domain.
 *
 * @param {HTMLElement} container The tab container element.
 * @param {string} typeLabel User-facing entity label.
 * @param {string} catKey Config key for category groups.
 * @param {string} statusKey Config key for statuses.
 * @param {?string} [typeKey=null] Config key for types.
 * @param {?string} [providerKey=null] Config key for provider options.
 * @param {?string} [payKey=null] Config key for payment options.
 * @param {?string} [issueKey=null] Config key for issue statuses.
 * @returns {void}
 */
export function renderCategoryConfigTab(
    container,
    typeLabel,
    catKey,
    statusKey,
    typeKey = null,
    providerKey = null,
    payKey = null,
    issueKey = null,
) {
    const categories = tempConfigData?.[catKey] || [];
    const statuses = tempConfigData?.[statusKey] || [];
    let html = `<div class="max-w-5xl space-y-8">`;
    html += `
        <div class="bg-white p-6 rounded-lg shadow-md">
            <h4 class="text-lg font-semibold mb-4 border-b pb-2">${typeLabel} Status Groups</h4>
            ${renderCategoryEditor("Status Groups", catKey, categories)}
        </div>
        <div class="bg-white p-6 rounded-lg shadow-md">
            <h4 class="text-lg font-semibold mb-4 border-b pb-2">${typeLabel} Statuses</h4>
            ${renderOptionEditor("Status Options", statusKey, statuses, true, categories)}
        </div>
    `;

    if (typeKey) {
        if (!tempConfigData[typeKey]) {tempConfigData[typeKey] = [];}
        html += `
            <div class="bg-white p-6 rounded-lg shadow-md">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">${typeLabel} Types</h4>
                ${renderTypeEditor("Types", typeKey, tempConfigData[typeKey])}
            </div>
        `;
    }

    if (typeLabel === "Task") {
        html += renderTaskSettings();

        if (state.currentUserProfile?.role === "admin") {
            html += `
                <div class="bg-white p-6 rounded-lg shadow-md">
                    <h4 class="text-lg font-semibold mb-4 border-b pb-2">Task Linking</h4>
                    <div id="task-linking-tool-wrapper"></div>
                </div>
            `;
        }
    }

    if (providerKey || payKey || typeLabel === "Patient") {
        html += `
            <div class="bg-white p-6 rounded-lg shadow-md">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Additional Options</h4>
                ${typeLabel === "Patient" ? `
                    <div class="settings-patient-default-row mb-6">
                        <label class="font-medium">Default Case Manager</label>
                        <input type="hidden" data-config-key="defaultPatientCaseManager" value="${tempConfigData?.defaultPatientCaseManager || ""}">
                        <div class="settings-patient-default-control" data-patient-case-manager-control></div>
                        <p class="settings-helper-text settings-patient-default-helper">Used as the default case manager when creating a new patient file. Users can still change it before saving.</p>
                    </div>
                ` : ""}
                ${providerKey ? renderSimpleListEditor("Providers", providerKey, tempConfigData?.[providerKey] || []) : ""}
                ${payKey ? renderSimpleListEditor("Payment Methods", payKey, tempConfigData?.[payKey] || []) : ""}
            </div>
        `;
    }

    if (issueKey && typeLabel !== "Asset") {
        html += `
            <div class="bg-white p-6 rounded-lg shadow-md">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Issue Statuses</h4>
                ${renderOptionEditor("Issue Status Options", issueKey, tempConfigData?.[issueKey] || [], true, null)}
            </div>
        `;
    }

    html += `</div>`;
    container.innerHTML = html;
    bindStatusPrioritySettingsControls(container);
    bindTypeSettingsControls(container);
    bindTaskSettingsControls(container);
    bindPatientCaseManagerControl(container);

    document.removeEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);
    document.addEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);

    if (typeLabel === "Task" && state.currentUserProfile?.role === "admin") {
        renderTaskLinkingTool(container);
    }
}

/**
 * Renders a status/type settings surface for future parent-board modules.
 *
 * @param {HTMLElement} container The tab container element.
 * @param {object} config Module settings config.
 * @param {string} config.typeLabel User-facing module label.
 * @param {string} config.statusKey Config key for status options.
 * @param {string} config.typeKey Config key for type options.
 * @returns {void}
 */
export function renderModuleConfigTab(
    container,
    {
        typeLabel,
        statusKey,
        typeKey,
    },
) {
    if (!tempConfigData[statusKey]) {tempConfigData[statusKey] = [];}
    if (!tempConfigData[typeKey]) {tempConfigData[typeKey] = [];}

    container.innerHTML = `
        <div class="max-w-5xl space-y-8">
            <div class="bg-white p-6 rounded-lg shadow-md">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">${typeLabel} Statuses</h4>
                ${renderOptionEditor("Status Options", statusKey, tempConfigData[statusKey], true, null)}
            </div>
            <div class="bg-white p-6 rounded-lg shadow-md">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">${typeLabel} Types</h4>
                ${renderTypeEditor("Types", typeKey, tempConfigData[typeKey])}
            </div>
        </div>
    `;

    bindStatusPrioritySettingsControls(container);
    bindTypeSettingsControls(container);

    document.removeEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);
    document.addEventListener("click", closeTypeSettingsDropdownsOnOutsideClick);
}

/**
 * Applies input-driven edits inside category-driven settings tabs.
 *
 * @param {HTMLElement} target The event target.
 * @param {object} [options={}] Render and dirty-state callbacks.
 * @returns {boolean} True when the change was handled.
 */
export function handleCategorySettingsChange(target, options = {}) {
    const {
        currentSettingsTab,
        updateSaveButton = () => {},
        rerenderCurrentTabContent = () => {},
    } = options;

    if (target.closest("#user-deactivation-draft")) {return false;}

    if (target.matches("[data-task-quick-close-status-control]")) {
        updateSaveButton(true);
        rerenderCurrentTabContent();
        return true;
    }

    if (target.matches("input, select") && target.dataset.configKey) {
        const configKey = target.dataset.configKey;
        tempConfigData[configKey] = target.type === "checkbox"
            ? target.checked
            : target.type === "number"
                ? (target.value === "" ? null : Number(target.value))
                : target.value || null;

        updateSaveButton(true);
        if (
            [
                "tasks",
                "patients",
                "labs",
                "projects",
                "assets",
                "maintenance",
            ].includes(currentSettingsTab)
        ) {
            rerenderCurrentTabContent();
        }
        return true;
    }

    if (handleTypeSettingsChange(target, {
        rerenderCurrentTabContent,
        updateSaveButton,
    })) {
        return true;
    }

    if (
        !target.matches("input, select") ||
        !target.closest("[data-key]") ||
        target.dataset.index === undefined
    ) {
        return false;
    }

    const container = target.closest("[data-key]");
    const key = container.dataset.key;
    const index = parseInt(target.dataset.index, 10);
    const prop = target.dataset.prop;

    if (!tempConfigData?.[key] || tempConfigData[key][index] === undefined) {
        return false;
    }

    const item = tempConfigData[key][index];
    if (prop) {
        if (typeof item !== "object") {return false;}

        switch (target.type) {
            case "radio":
                if (prop === "default") {
                    tempConfigData[key].forEach((otherItem, itemIndex) => {
                        if (typeof otherItem === "object") {
                            otherItem.default = itemIndex === index;
                        }
                    });
                }
                break;
            case "checkbox":
                if (prop === "streams") {
                    if (!updateStreamScope(
                        item,
                        key,
                        target.value,
                        target.checked,
                    )) {
                        rerenderCurrentTabContent();
                        return true;
                    }
                    rerenderCurrentTabContent();
                } else {
                    item[prop] = target.checked;
                }
                break;
            case "number":
                item[prop] = parseInt(target.value, 10) || 0;
                break;
            case "color": {
                item[prop] = target.value;
                syncLinkedColourFields(target, target.value);
                break;
            }
            case "text":
                if (prop === "color") {
                    item[prop] = target.value;
                    syncLinkedColourFields(target, target.value);
                } else if (prop !== "id") {
                    item[prop] = target.value;
                }
                break;
            default:
                item[prop] = target.value;
        }
    } else {
        if (typeof item !== "string") {return false;}
        tempConfigData[key][index] = target.value;
    }

    updateSaveButton(true);
    return true;
}

/**
 * Handles button actions inside category-driven settings tabs.
 *
 * @param {HTMLElement} target The clicked element.
 * @param {object} [options={}] Render and dirty-state callbacks.
 * @returns {Promise<boolean>} True when the click was handled.
 */
export async function handleCategorySettingsButtonClick(target, options = {}) {
    if (await handleTypeSettingsButtonClick(target, options)) {
        return true;
    }

    if (await handleStatusPrioritySettingsButtonClick(target, options)) {
        return true;
    }

    return false;
}
